import React, { useEffect, useState } from 'react';
import { Form, Input, Select } from 'antd';
import styles from './index.scss';
import { trim, capitalize } from 'lodash';
import Validator from 'validatorjs';

const { TextArea } = Input;
const { Option, OptGroup } = Select;

export const MatchingConfigName = ({
  saveMatchingConfigToStore,
  values,
  handleChange,
  reset,
  childReset,
  handleOnBlur,
  disabled,
  modulesList,
  disabledDropDown
}) => {
  const [form] = Form.useForm();

  const [errors, setErrors] = useState({
    name: '',
    description: '',
    sourceIds: '',
    notes: ''
  });

  const validatingRegex = (pattern, value) => {
    let newPattern = pattern
      ? pattern
      : '/^([a-zA-Z0-9]){0,1}([a-zA-Z0-9\\s_\\-\\.\\,]){0,999}$/'; // Regex for TextArea
    let regex = new RegExp(trim(newPattern, '/'));
    return regex.test(value.trimEnd());
  };

  const onChangeHandler = (e) => {
    const { name, value, pattern, min, max, minLength, maxLength } = e.target;
    let maximumLength = e.target.nodeName == 'TEXTAREA' ? 1000 : maxLength;
    handleChange(name, value);
    let newData = {
      ...values,
      [name]: value
    };
    if (/^[\s_\-]$/.test(value.charAt(0))) {
      // for space and special characters in starting of string
      setErrors({
        ...errors,
        [name]: capitalize(
          `${name} cannot start with space or special characters`
        )
      });
    } else if (!validatingRegex(pattern, value)) {
      // for Regex validation
      setErrors({
        ...errors,
        [name]:
          e.target.nodeName == 'TEXTAREA'
            ? `Must be ${min || minLength}-${
                max || maximumLength
              } characters (a-z A-Z 0-9 _ - . , space)`
            : `Must be ${min}-${max} characters (a-z A-Z 0-9 _ - space)`
      });
    } else
      setErrors({
        ...errors,
        [name]: ''
      });
    checkFormValidation(newData);
  };

  const moduleSelectHandler = (val) => {
    handleChange('sourceIds', val);
    let newData = {
      ...values,
      sourceIds: val
    };
    setErrors({
      ...errors,
      sourceIds: ''
    });
    checkFormValidation(newData);
  };

  const onBlurSelectHanlder = () => {
    if (values?.sourceIds !== '')
      setErrors({
        ...errors,
        sourceIds: ''
      });
    else
      setErrors({
        ...errors,
        sourceIds: 'Please select matching configuration module.'
      });
  };

  const onBlur = (e) => {
    const { name, value, required } = e.target;
    if (value === '' && required) {
      // for Required validation
      setErrors({
        ...errors,
        [name]: `Please input matching configuration ${name}`
      });
      handleOnBlur();
    }
  };

  const checkFormValidation = (newData) => {
    let rules = {
      name: ['required', 'regex:/^[a-zA-Z0-9]([a-zA-Z0-9_\\s\\-]){2,99}$/'],
      description: [
        'required',
        'regex:/^[a-zA-Z0-9]([a-zA-Z0-9\\s_\\-]){2,199}$/'
      ],
      sourceIds: ['required'],
      notes: ['regex:/^[a-zA-Z0-9]([a-zA-Z0-9\\s_\\-\\.,]){0,999}$/']
    };

    let validation = new Validator(newData, rules);
    if (saveMatchingConfigToStore)
      saveMatchingConfigToStore(
        'matchingConfigurationName',
        newData,
        validation.passes()
      );
  };

  useEffect(() => {
    if (reset) {
      setErrors({
        name: '',
        description: '',
        sourceIds: '',
        notes: ''
      });
      childReset(false);
    }
  }, [reset]);

  useEffect(() => {
    if (disabled)
      checkFormValidation({
        name: values?.name,
        description: values?.description,
        notes: values?.notes,
        sourceIds: values?.sourceIds
      });
  }, []);

  return (
    <Form className={styles.form} initialValues={values} form={form}>
      <div style={{ maxWidth: '256px', paddingTop: '24px' }}>
        <label className={(styles.label, styles.required)}>
          Matching Configuration Name
        </label>
        <Input
          name="name"
          id={'matchingConfigurationName-input'}
          title=""
          className={styles.input}
          value={values?.name}
          onChange={onChangeHandler}
          placeholder="Matching Configuration Name"
          onBlur={onBlur}
          required={true}
          min={3}
          max={100}
          pattern={/^[a-zA-Z0-9]([a-zA-Z0-9\s_\-]){2,99}$/}
          disabled={disabled}
        />
        {errors.name !== '' && (
          <p className={styles.errorMessage} id="error_name">
            {errors.name}
          </p>
        )}
      </div>
      <div style={{ maxWidth: '556px' }}>
        <label className={(styles.label, styles.required)}>
          Matching Configuration Description
        </label>
        <Input
          name="description"
          id={'matchingConfigurationName-description'}
          title=""
          className={styles.input}
          value={values?.description}
          onChange={onChangeHandler}
          placeholder="Matching Configuration Description"
          onBlur={onBlur}
          required={true}
          min={3}
          max={200}
          pattern={/^[a-zA-Z0-9]([a-zA-Z0-9\s_\-]){2,199}$/}
          disabled={disabled}
        />
        {errors.description !== '' && (
          <p className={styles.errorMessage} id="error_description">
            {errors.description}
          </p>
        )}
      </div>

      <div
        style={{ maxWidth: '256px', display: 'flex', flexDirection: 'column' }}>
        <label className={(styles.label, styles.required)}>Module</label>
        <Select
          placeholder="Select Module"
          value={values?.sourceIds ? values?.sourceIds : null}
          id="moduleType"
          dropdownClassName="moduleType"
          onChange={moduleSelectHandler}
          onBlur={onBlurSelectHanlder}
          disabled={disabledDropDown}>
          <OptGroup label="Select Module">
            {modulesList?.map((item, index) => (
              <Option value={item.value} key={index}>
                {item.label}
              </Option>
            ))}
          </OptGroup>
        </Select>
        {errors.sourceIds !== '' && (
          <p className={styles.errorMessage} id="error_module">
            {errors.sourceIds}
          </p>
        )}
      </div>

      <div style={{ maxWidth: '556px' }}>
        <label className={styles.label}>Notes</label>
        <TextArea
          type="text"
          id={'matchingConfigurationName-notes'}
          name="notes"
          className={styles.input}
          value={values?.notes}
          onChange={onChangeHandler}
          placeholder="Please enter your notes here"
          rows="6"
          cols="50"
          minLength={0}
          maxLength={1000}
          disabled={disabled}
        />
        {errors.notes !== '' && (
          <p className={styles.errorMessage} id="error_notes">
            {errors.notes}
          </p>
        )}
      </div>
    </Form>
  );
};

export default MatchingConfigName;
